import { resolveCookieDomain } from './visitor-id';
import { TRACKING_PARAM } from './tracking-params';

/**
 * Carrying the visitor id in the LINK, because the browser forgets.
 *
 * `resolveVisitorId()` has always accepted `?hamla_vid=` and preferred it over
 * both stores. Nothing ever put it there, so the parameter existed and was only
 * ever set by hand for email and SMS links. This is the other half.
 *
 * WHY A LINK AND NOT THE COOKIE. The cookie is written on the registrable
 * domain, so `www.example.com` → `app.example.com` is exactly the case it was
 * designed for, and in an ordinary browser it works. In an in-app webview it
 * does not reliably survive at all: measured on our own traffic, one visitor
 * arriving from an Instagram ad was issued TWO different ids ten seconds apart
 * on the identical URL, before touching a second host. Storage there is
 * partitioned or discarded per navigation, and no cookie attribute changes
 * that.
 *
 * A URL survives what storage does not — every navigation, every redirect, an
 * OAuth round trip, a storage wipe. So the id travels as data on the link the
 * visitor is already clicking.
 *
 * WHAT GETS DECORATED, BY DEFAULT AND WITHOUT CONFIGURATION: a link to a
 * DIFFERENT host that shares this site's registrable domain. That is the
 * marketing-site-to-app hop every business with a `app.` or `my.` subdomain
 * has, and it is precisely where the cookie is supposed to work and sometimes
 * doesn't. Truly separate domains have to be named in `crossDomain`, because
 * appending an id to a host we do not own would leak it.
 *
 * Same-host links are never touched: nothing is crossing, and the id would just
 * be noise in the address bar of every page.
 */

/** The parameter `resolveVisitorId()` already reads. */
export const VISITOR_URL_PARAM = TRACKING_PARAM.VISITOR_ID;

export interface LinkDecoratorOptions {
  /** Called at click time, so a freshly-minted id is used rather than a stale one. */
  getVisitorId: () => string | null;
  /** Extra hosts to decorate — separate domains this business also owns. */
  extraHosts?: string[];
}

/**
 * Should a link to `targetHost` carry the visitor id?
 *
 * Pure and exported so the rule is testable without a DOM: the interesting
 * cases are a public suffix (`example.co.uk`), a look-alike host
 * (`nothamla.io`), and the same host (never decorate).
 */
export function shouldDecorate(
  targetHost: string | null,
  currentHost: string,
  registrableDomain: string | null,
  extraHosts: string[] = [],
): boolean {
  if (!targetHost || targetHost === currentHost) return false;
  if (extraHosts.includes(targetHost)) return true;
  if (!registrableDomain) return false;

  // `.` prefixed so `nothamla.io` cannot match a registrable domain of
  // `hamla.io` by a bare suffix test.
  return (
    targetHost === registrableDomain ||
    targetHost.endsWith(`.${registrableDomain}`)
  );
}

/**
 * Return `rawUrl` carrying the visitor id, or unchanged when it cannot.
 *
 * An id already on the URL WINS and is never overwritten — an email or SMS link
 * naming a specific visitor is better evidence than whatever this browser
 * happens to hold, and that precedence already exists in `resolveVisitorId`.
 */
export function decorateUrl(
  rawUrl: string,
  visitorId: string | null,
  base?: string,
): string {
  if (!visitorId) return rawUrl;

  try {
    const url = new URL(rawUrl, base);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return rawUrl;
    if (url.searchParams.has(VISITOR_URL_PARAM)) return rawUrl;

    url.searchParams.set(VISITOR_URL_PARAM, visitorId);
    return url.toString();
  } catch {
    // A `mailto:`, a `tel:`, or something unparseable. Leave it alone.
    return rawUrl;
  }
}

/**
 * Decorate qualifying links as they are clicked.
 *
 * At CLICK time rather than on load, for two reasons that both bit us: links
 * rendered after hydration (every React site) would never be seen by a one-shot
 * pass, and an id minted after page load would be missing from links stamped
 * before it existed.
 *
 * Capture phase, so the href is already correct when any other handler or the
 * framework's router reads it.
 *
 * Returns a teardown. Never throws — a marketing site must not break because
 * attribution could not be attached.
 */
export function installLinkDecorator(
  options: LinkDecoratorOptions,
): () => void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => {};
  }

  const extraHosts = options.extraHosts ?? [];
  // Probed once: it sets and clears a cookie, which is not something to repeat
  // on every click.
  let registrableDomain: string | null = null;
  let probed = false;

  const onClick = (event: Event) => {
    try {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      if (!probed) {
        registrableDomain = resolveCookieDomain();
        probed = true;
      }

      const url = new URL(href, window.location.href);
      if (
        !shouldDecorate(
          url.hostname,
          window.location.hostname,
          registrableDomain,
          extraHosts,
        )
      ) {
        return;
      }

      const decorated = decorateUrl(url.toString(), options.getVisitorId());
      if (decorated !== url.toString()) anchor.setAttribute('href', decorated);
    } catch {
      // Never let attribution break a navigation.
    }
  };

  // `auxclick` too: a middle-click opens the link in a new tab and is exactly
  // the case where the new context starts with no storage at all.
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('auxclick', onClick, true);
  };
}
